import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Select,
  Input,
  Space,
  Tag,
  Typography,
  Alert,
  message,
  Popconfirm,
  Tooltip,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  CheckCircleOutlined,
  EditOutlined,
  EyeOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { legalAdminService } from '../../../services/legalAdmin.service';
import { Notice } from '../../../components/common/Notice';
import type {
  LegalDocumentAdminItem,
  LegalDocumentType,
  LegalDocumentStatus,
} from '../../../types/legalConsent.types';

const { Title, Text } = Typography;
const { TextArea } = Input;

const TIPO_LABELS: Record<LegalDocumentType, string> = {
  TERMS_OF_SERVICE: 'Termos de Uso',
  PRIVACY_POLICY: 'Política de Privacidade',
  COOKIE_POLICY: 'Política de Cookies',
};

const STATUS_CONFIG: Record<LegalDocumentStatus, { label: string; color: string }> = {
  DRAFT: { label: 'Rascunho', color: 'orange' },
  ACTIVE: { label: 'Ativo', color: 'green' },
  OBSOLETE: { label: 'Obsoleto', color: 'default' },
};

type ModalMode = 'create' | 'edit' | 'view';

interface DocumentModalState {
  open: boolean;
  mode: ModalMode;
  source?: LegalDocumentAdminItem;
}

export const LegalDocumentsPage: React.FC = () => {
  const [documents, setDocuments] = useState<LegalDocumentAdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<LegalDocumentType | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<LegalDocumentStatus | undefined>(undefined);
  const [modal, setModal] = useState<DocumentModalState>({ open: false, mode: 'create' });
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await legalAdminService.listDocuments({
        type: typeFilter,
        status: statusFilter,
      });
      setDocuments(items);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar documentos';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const closeModal = () => setModal({ open: false, mode: 'create' });

  const openCreateModal = () => {
    form.resetFields();
    setModal({ open: true, mode: 'create' });
  };

  const openEditModal = (doc: LegalDocumentAdminItem) => {
    form.setFieldsValue({ type: doc.type, contentHtml: doc.contentHtml });
    setModal({ open: true, mode: 'edit', source: doc });
  };

  const openViewModal = (doc: LegalDocumentAdminItem) => {
    setModal({ open: true, mode: 'view', source: doc });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await legalAdminService.createDraft({
        type: values.type as LegalDocumentType,
        contentHtml: values.contentHtml as string,
      });
      message.success('Rascunho criado com sucesso');
      closeModal();
      loadDocuments();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      const msg = err instanceof Error ? err.message : 'Erro ao salvar documento';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (documentId: string) => {
    try {
      setPublishing(documentId);
      await legalAdminService.publish(documentId);
      message.success('Documento publicado com sucesso');
      loadDocuments();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao publicar documento';
      message.error(msg);
    } finally {
      setPublishing(null);
    }
  };


  const columns: ColumnsType<LegalDocumentAdminItem> = [
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      render: (type: LegalDocumentType) => TIPO_LABELS[type],
      filters: [
        { text: 'Termos de Uso', value: 'TERMS_OF_SERVICE' },
        { text: 'Política de Privacidade', value: 'PRIVACY_POLICY' },
        { text: 'Política de Cookies', value: 'COOKIE_POLICY' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Versão',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Data de Criação',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Publicado em',
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      render: (date: string | null) => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Situação',
      dataIndex: 'status',
      key: 'status',
      render: (status: LegalDocumentStatus) => {
        const { label, color } = STATUS_CONFIG[status];
        return <Tag color={color}>{label}</Tag>;
      },
      filters: [
        { text: 'Rascunho', value: 'DRAFT' },
        { text: 'Ativo', value: 'ACTIVE' },
        { text: 'Obsoleto', value: 'OBSOLETE' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Visualizar">
            <Button size="small" icon={<EyeOutlined />} onClick={() => openViewModal(record)} />
          </Tooltip>

          {record.status === 'DRAFT' && (
            <>
              <Tooltip title="Editar (nova versão)">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openEditModal(record)}
                />
              </Tooltip>
              <Popconfirm
                title="Publicar documento"
                description="Ao publicar, o documento ativo do mesmo tipo será desativado. Confirmar?"
                onConfirm={() => handlePublish(record.id)}
                okText="Publicar"
                cancelText="Cancelar"
              >
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={publishing === record.id}
                >
                  Publicar
                </Button>
              </Popconfirm>
            </>
          )}

          {record.status === 'ACTIVE' && (
            <Tooltip title="Criar nova versão baseada neste documento">
              <Button size="small" icon={<HistoryOutlined />} onClick={() => openEditModal(record)}>
                Nova Versão
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>
            Termos e Política de Privacidade
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Novo Documento
          </Button>
        </Space>

        <Space wrap>
          <Select
            placeholder="Filtrar por tipo"
            allowClear
            style={{ width: 220 }}
            value={typeFilter}
            onChange={(v) => setTypeFilter(v)}
            options={[
              { value: 'TERMS_OF_SERVICE', label: 'Termos de Uso' },
              { value: 'PRIVACY_POLICY', label: 'Política de Privacidade' },
              { value: 'COOKIE_POLICY', label: 'Política de Cookies' },
            ]}
          />
          <Select
            placeholder="Filtrar por situação"
            allowClear
            style={{ width: 180 }}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            options={[
              { value: 'DRAFT', label: 'Rascunho' },
              { value: 'ACTIVE', label: 'Ativo' },
              { value: 'OBSOLETE', label: 'Obsoleto' },
            ]}
          />
        </Space>

        {error && (
          <Alert
            type="error"
            message="Erro ao carregar documentos"
            description={error}
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        <Table
          columns={columns}
          dataSource={documents}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          locale={{ emptyText: 'Nenhum documento encontrado' }}
        />
      </Space>

      <Modal
        title={
          modal.mode === 'view'
            ? `Visualizar: ${modal.source ? TIPO_LABELS[modal.source.type] : ''}`
            : modal.mode === 'create'
              ? 'Novo Documento Legal'
              : 'Nova Versão do Documento'
        }
        open={modal.open}
        onCancel={closeModal}
        footer={
          modal.mode === 'view'
            ? [
                <Button key="close" onClick={closeModal}>
                  Fechar
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={closeModal}>
                  Cancelar
                </Button>,
                <Button key="save" type="primary" loading={saving} onClick={handleSave}>
                  Salvar Rascunho
                </Button>,
              ]
        }
        width={modal.mode === 'view' ? 860 : 800}
        destroyOnClose
      >
        {modal.mode === 'view' && modal.source && (
          <>
            <Space style={{ marginBottom: 16 }} wrap>
              <Text type="secondary">
                Versão: <Text strong>{modal.source.version}</Text>
              </Text>
              <Text type="secondary">
                Situação:{' '}
                <Tag color={STATUS_CONFIG[modal.source.status].color}>
                  {STATUS_CONFIG[modal.source.status].label}
                </Tag>
              </Text>
              {modal.source.publishedAt && (
                <Text type="secondary">
                  Publicado em:{' '}
                  <Text strong>{dayjs(modal.source.publishedAt).format('DD/MM/YYYY')}</Text>
                </Text>
              )}
            </Space>
            <Divider style={{ margin: '12px 0 20px' }} />
            <div
              style={{
                maxHeight: 560,
                overflowY: 'auto',
                paddingRight: 8,
                lineHeight: 1.7,
                fontSize: 14,
              }}
              dangerouslySetInnerHTML={{ __html: modal.source.contentHtml }}
            />
          </>
        )}

        {modal.mode !== 'view' && (
          <>
            {modal.mode === 'edit' && modal.source && (
              <>
                <Notice
                  type="info"
                  showIcon
                  message={`Criando nova versão baseada em: ${TIPO_LABELS[modal.source.type]} v${modal.source.version}`}
                  style={{ marginBottom: 16 }}
                />
                <Divider />
              </>
            )}

            <Form form={form} layout="vertical">
              <Form.Item
                name="type"
                label="Tipo de Documento"
                rules={[{ required: true, message: 'Selecione o tipo de documento' }]}
              >
                <Select
                  placeholder="Selecione o tipo"
                  disabled={modal.mode === 'edit'}
                  options={[
                    { value: 'TERMS_OF_SERVICE', label: 'Termos de Uso' },
                    { value: 'PRIVACY_POLICY', label: 'Política de Privacidade' },
                    { value: 'COOKIE_POLICY', label: 'Política de Cookies' },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="contentHtml"
                label="Conteúdo (HTML)"
                rules={[{ required: true, message: 'Informe o conteúdo do documento' }]}
              >
                <TextArea
                  rows={16}
                  placeholder="<h1>Termos de Uso</h1><p>...</p>"
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};
